/**
 * Which card a rank IS.
 *
 * The ranks are named after the deck (Deuce … Wild), so the badge is not a
 * symbol standing for a rank — it is that rank's card, printed from the same
 * SVG the table deals. Two of them need saying out loud:
 *
 * - **Blackjack** is not one card. It is the hand: an ace and a jack, which the
 *   ceremony fans apart into a pair.
 * - **Wild** is the jack of spades, because in 21 Wild the wild cards ARE the
 *   black jacks (`WILD_CARDS`). Inventing a joker for the top rank would put a
 *   card on the badge that never appears in a round; this one is the most
 *   powerful card a player actually holds, which is the whole point of the rank.
 */

import type { CardId, Rank as CardRank, Suit } from '@/game/types';
import { RANK_NAMES } from '@/game/xp';

/** The deck rank each XP rank wears, by index into `RANK_NAMES`. */
export const RANK_FACE: readonly CardRank[] = ['2', '7', '10', 'j', 'q', 'k', 'a', 'a', 'j'];

/** Blackjack's second card: the jack beside the ace. */
export const BLACKJACK_PARTNER: CardRank = 'j';

/** The index of the Wild rank — the one whose card really is wild in play. */
export const WILD_RANK = 8;

/** The suit the hero card is printed in. Spades: the game's own wild suit. */
export const HERO_SUIT: Suit = 's';

/** The other three suits, for the fan behind the hero. */
export const FAN_SUITS: readonly Suit[] = ['h', 'd', 'c'];

function clampIndex(index: number): number {
  return Math.min(Math.max(0, Math.floor(index)), RANK_NAMES.length - 1);
}

/** The card a rank shows: its own deck rank, in the suit asked for. */
export function rankCard(rankIndex: number, suit: Suit = HERO_SUIT): CardId {
  return `${RANK_FACE[clampIndex(rankIndex)]}${suit}` as CardId;
}

/** Blackjack's partner card, or null for every other rank. */
export function partnerCard(rankIndex: number): CardId | null {
  return clampIndex(rankIndex) === 7 ? (`${BLACKJACK_PARTNER}${HERO_SUIT}` as CardId) : null;
}

/** Every plate a ceremony between two ranks needs baking. */
export function platesFor(outIndex: number, inIndex: number): CardId[] {
  const ids = new Set<CardId>([rankCard(outIndex), rankCard(inIndex)]);
  for (const suit of FAN_SUITS) ids.add(rankCard(inIndex, suit));
  const partner = partnerCard(inIndex);
  if (partner) ids.add(partner);
  return [...ids];
}
